import { useMemo } from 'react';
import { MenuItem } from 'primereact/menuitem';
import { useAccountPreference } from './useAccountPreference';

/**
 * Abbreviate account name: "Bank of America Trust - 3582" -> "BofAT - 3582"
 */
const abbreviateAccountName = (name: string): string => {
  const parts = name.split(' - ');
  if (parts.length < 2) return name;

  const bankName = parts[0];
  const accountNum = parts[parts.length - 1];
  const minorWords = ['of', 'and', 'the', 'for'];

  const abbreviation = bankName
    .split(' ')
    .map((word) => {
      const firstLetter = word[0];
      return minorWords.includes(word.toLowerCase())
        ? firstLetter.toLowerCase()
        : firstLetter.toUpperCase();
    })
    .join('');

  return `${abbreviation} - ${accountNum}`;
};

export interface UseAccountMenuItemOptions {
  /** Include "All accounts" option */
  showAllOption?: boolean;
  /** Use abbreviated name for the menu label */
  abbreviateLabel?: boolean;
  /** Custom account list (overrides accounts from useAccountPreference) */
  customAccounts?: { id: string; name: string }[];
  /** Custom setter for account selection (for use with customFilters) */
  customSetAccount?: (accountId: string | null) => void;
  /** Custom selected account ID (for use with customFilters) */
  customSelectedId?: string | null;
}

export interface UseAccountMenuItemResult {
  /** Menu item ready to spread into Menubar's model array */
  accountMenuItem: MenuItem;
  /** Current selected account ID (null means "all") */
  selectedAccountId: string | null;
  /** Whether accounts are still loading */
  isLoading: boolean;
}

/**
 * Hook that returns a menu item for account selection, compatible with PrimeReact Menubar.
 *
 * @example
 * const { accountMenuItem } = useAccountMenuItem(clientId, { showAllOption: true, abbreviateLabel: true });
 * const menuItems = [accountMenuItem, ...otherItems];
 * <Menubar model={menuItems} />
 */
export function useAccountMenuItem(
  clientId: string,
  options: UseAccountMenuItemOptions = {}
): UseAccountMenuItemResult {
  const {
    showAllOption = false,
    abbreviateLabel = true,
    customAccounts,
    customSetAccount,
    customSelectedId,
  } = options;
  const { selectedAccountId: hookSelectedId, setSelectedAccountId: hookSetAccount, accounts: hookAccounts, isLoading } = useAccountPreference(clientId);

  // Use custom values if provided, otherwise fall back to hook values
  const accounts = customAccounts ?? hookAccounts;
  const selectedAccountId = customSelectedId !== undefined ? customSelectedId : hookSelectedId;
  const setSelectedAccountId = customSetAccount ?? hookSetAccount;

  const accountMenuItem = useMemo<MenuItem>(() => {
    const currentLabel = (() => {
      if (!selectedAccountId) return 'All accounts';
      const account = accounts.find((a) => a.id === selectedAccountId);
      if (!account) return 'All accounts';
      return abbreviateLabel ? abbreviateAccountName(account.name) : account.name;
    })();

    const items: MenuItem[] = [
      ...(showAllOption
        ? [{ label: 'All accounts', command: () => setSelectedAccountId(null) }]
        : []),
      ...accounts.map((account) => ({
        label: account.name,
        command: () => setSelectedAccountId(account.id),
      })),
    ];

    return {
      label: currentLabel,
      icon: 'ms-out ms-out-account-balance',
      items,
    };
  }, [selectedAccountId, accounts, showAllOption, abbreviateLabel, setSelectedAccountId]);

  return {
    accountMenuItem,
    selectedAccountId,
    isLoading,
  };
}
